import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentControllerXService, DocumentDescriptionDTO } from '@iota/iota-management-portal-v4-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, Observable, of, take, tap } from 'rxjs';
import {
    DeleteCentralRegulatorLogo,
    DeleteCentralRegulatorLogoExtra,
    DeleteCompanyLogo,
    DeleteCompanyLogoExtra,
    DeleteCompanyLogoExtraSuccess,
    DeleteCompanyLogoSuccess,
    DeleteHealthInsurerLogo,
    DeleteHealthInsurerLogoFailure,
    DeleteHealthInsurerLogoSuccess,
    DocumentActionTypes,
    DownloadDocument,
    DownloadHealthInsurerLogo,
    LoadCentralRegulatorLogo,
    LoadCentralRegulatorLogoExtra,
    LoadCentralRegulatorLogoExtraSuccess,
    LoadCentralRegulatorLogoSuccess,
    LoadCompanyLogo,
    LoadCompanyLogoExtra,
    LoadCompanyLogoExtraSuccess,
    LoadCompanyLogoSuccess,
    LoadDocument,
    LoadHealthInsurerLogo,
    LoadProfilePicture,
    UploadCentralRegulatorLogo,
    UploadCentralRegulatorLogoExtra,
    UploadCompanyLogo,
    UploadCompanyLogoExtra,
    UploadCompanyLogoExtraSuccess,
    UploadCompanyLogoSuccess,
    UploadHealthInsurerLogo
} from '../actions/document.action';
import {
    centralRegulatorLogoById,
    centralRegulatorLogoExtraById,
    companyLogoById,
    companyLogoExtraById,
    DocumentDescriptionWithUrl,
    healthInsurerLogoById,
    profilePictureById
} from '../reducer/document.reducer';
import * as saveAs from 'file-saver';

@Injectable()
export class DocumentEffects {
    loadCompanyLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyLogo),
            concatLatestFrom(({ companyId }) => this.store.select(companyLogoById(companyId))),
            mergeMap(([{ companyId }, logo]) => {
                if (!logo.url) {
                    return this.loadCompanyLogo(companyId).pipe(
                        map((result) => LoadCompanyLogoSuccess({ companyLogoUrl: result, companyId })),
                        catchError((error) => of({ type: DocumentActionTypes.LoadCompanyLogoFailure, error: error, companyId }))
                    );
                }
                return of({ type: DocumentActionTypes.LoadCompanyLogoSuccess, companyLogoUrl: logo, companyId });
            })
        )
    );

    loadCompanyLogoNoCache$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCompanyLogoSuccess, UploadCompanyLogoSuccess),
            concatLatestFrom(({ companyId }) => this.store.select(companyLogoById(companyId))),
            mergeMap(([{ companyId }, logo]) => {
                if (!logo.url) {
                    return this.loadCompanyLogoNoCache(companyId).pipe(
                        map((result) => LoadCompanyLogoSuccess({ companyLogoUrl: result, companyId })),
                        catchError((error) => of({ type: DocumentActionTypes.LoadCompanyLogoFailure, error: error, companyId }))
                    );
                }
                return of({ type: DocumentActionTypes.LoadCompanyLogoSuccess, companyLogoUrl: logo, companyId });
            })
        )
    );

    loadCompanyLogoExtra$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyLogoExtra),
            concatLatestFrom(({ companyId }) => this.store.select(companyLogoExtraById(companyId))),
            mergeMap(([{ companyId }, logo]) => {
                if (!logo.url) {
                    return this.loadCompanyLogoExtra(companyId).pipe(
                        map((result) => LoadCompanyLogoExtraSuccess({ companyLogoExtraUrl: result, companyId })),
                        catchError((error) =>
                            of({
                                type: DocumentActionTypes.LoadCompanyLogoExtraFailure,
                                error: error,
                                companyId
                            })
                        )
                    );
                }
                return of({ type: DocumentActionTypes.LoadCompanyLogoExtraSuccess, companyLogoExtraUrl: logo, companyId });
            })
        )
    );

    loadCompanyLogoExtraNoCache$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCompanyLogoExtraSuccess, UploadCompanyLogoExtraSuccess),
            concatLatestFrom(({ companyId }) => this.store.select(companyLogoExtraById(companyId))),
            mergeMap(([{ companyId }, logo]) => {
                if (!logo.url) {
                    return this.loadCompanyLogoExtraNoCache(companyId).pipe(
                        map((result) => LoadCompanyLogoExtraSuccess({ companyLogoExtraUrl: result, companyId })),
                        catchError((error) =>
                            of({
                                type: DocumentActionTypes.LoadCompanyLogoExtraFailure,
                                error: error,
                                companyId
                            })
                        )
                    );
                }
                return of({ type: DocumentActionTypes.LoadCompanyLogoExtraSuccess, companyLogoExtraUrl: logo, companyId });
            })
        )
    );

    uploadCompanyLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadCompanyLogo),
            mergeMap(({ companyId, file }) =>
                this.uploadCompanyLogo(companyId, file).pipe(
                    map(() => ({ type: DocumentActionTypes.LoadCompanyLogo, companyId })),
                    catchError((error) => of({ type: DocumentActionTypes.UploadCompanyLogoFailure, error: error, companyId }))
                )
            )
        )
    );

    uploadCompanyLogoExtra$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadCompanyLogoExtra),
            mergeMap(({ companyId, file }) =>
                this.uploadCompanyLogoExtra(companyId, file).pipe(
                    map(() => ({ type: DocumentActionTypes.LoadCompanyLogoExtra, companyId })),
                    catchError((error) =>
                        of({
                            type: DocumentActionTypes.UploadCompanyLogoExtraFailure,
                            error: error,
                            companyId
                        })
                    )
                )
            )
        )
    );

    deleteCompanyLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCompanyLogo),
            mergeMap(({ companyId }) =>
                this.apiService.deleteCompanyLogo(companyId).pipe(
                    map(() => ({ type: DocumentActionTypes.DeleteCompanyLogoSuccess, companyId })),
                    catchError((error) => of({ type: DocumentActionTypes.DeleteCompanyLogoFailure, error: error }))
                )
            )
        )
    );

    deleteCompanyLogoExtra$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCompanyLogoExtra),
            mergeMap(({ companyId }) =>
                this.apiService.deleteCompanyLogoExtra(companyId).pipe(
                    map(() => ({ type: DocumentActionTypes.DeleteCompanyLogoExtraSuccess, companyId })),
                    catchError((error) => of({ type: DocumentActionTypes.DeleteCompanyLogoExtraFailure, error: error }))
                )
            )
        )
    );

    loadHealthInsurerLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadHealthInsurerLogo),
            concatLatestFrom(({ healthInsurerId }) => this.store.select(healthInsurerLogoById(healthInsurerId))),
            mergeMap(([{ healthInsurerId }, logo]) => {
                if (!logo.url) {
                    return this.loadHealthInsurerLogo(healthInsurerId).pipe(
                        map((result) => ({
                            type: DocumentActionTypes.LoadHealthInsurerLogoSuccess,
                            healthInsurerLogoUrl: result,
                            healthInsurerId
                        })),
                        catchError((error) =>
                            of({
                                type: DocumentActionTypes.LoadHealthInsurerLogoFailure,
                                error: error,
                                healthInsurerId
                            })
                        )
                    );
                }

                return of({
                    type: DocumentActionTypes.LoadHealthInsurerLogoSuccess,
                    healthInsurerLogoUrl: logo.url,
                    healthInsurerId
                });
            })
        )
    );

    uploadHealthInsurerLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadHealthInsurerLogo),
            mergeMap(({ healthInsurerId, file }) =>
                this.uploadHealthInsurerLogo(healthInsurerId, file).pipe(
                    map(() => ({ type: DocumentActionTypes.LoadHealthInsurerLogo, healthInsurerId })),
                    catchError((error) => of({ type: DocumentActionTypes.UploadHealthInsurerLogoFailure, error: error }))
                )
            )
        )
    );

    downloadHealthInsurerLogo$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DownloadHealthInsurerLogo),
                tap(({ healthInsurerId, fileName }) =>
                    this.downloadHealthInsurerLogo(healthInsurerId)
                        .pipe(take(1))
                        .subscribe((res) => saveAs(res, fileName))
                )
            ),
        { dispatch: false }
    );

    deleteHealthInsurerLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteHealthInsurerLogo),
            mergeMap(({ healthInsurerId }) =>
                this.apiService.deleteHealthInsurerLogo(healthInsurerId).pipe(
                    map(() => DeleteHealthInsurerLogoSuccess({ healthInsurerId })),
                    catchError((error) => of(DeleteHealthInsurerLogoFailure({ error })))
                )
            )
        )
    );

    loadDocumentUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadDocument),
            mergeMap(({ document }) =>
                this.loadDocumentAsSafeResourceUrl(document).pipe(
                    map((result) => ({
                        type: DocumentActionTypes.LoadDocumentSuccess,
                        document: { ...document, url: result } as DocumentDescriptionWithUrl
                    })),
                    catchError((error) => of({ type: DocumentActionTypes.LoadDocumentFailure, error: error }))
                )
            )
        )
    );

    downloadDocument$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DownloadDocument),
                tap(({ document }) =>
                    this.loadDocument(document)
                        .pipe(take(1))
                        .subscribe((res) => saveAs(res, document.name))
                )
            ),
        { dispatch: false }
    );

    loadProfilePicture$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadProfilePicture),
            concatLatestFrom(({ employeeId }) => this.store.select(profilePictureById(employeeId))),
            mergeMap(([{ employeeId }, profilePicture]) => {
                if (!profilePicture.url) {
                    return this.loadProfilePicture(employeeId).pipe(
                        take(1),
                        map((result) => ({
                            type: DocumentActionTypes.LoadProfilePictureSuccess,
                            employeeId: employeeId,
                            profilePictureUrl: result
                        })),
                        catchError((error) =>
                            of({ type: DocumentActionTypes.LoadProfilePictureFailure, employeeId: employeeId, error: error })
                        )
                    );
                }

                return of({
                    type: DocumentActionTypes.LoadProfilePictureSuccess,
                    profilePictureUrl: profilePicture.url,
                    employeeId
                });
            })
        )
    );

    loadCentralRegulatorLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCentralRegulatorLogo),
            concatLatestFrom(({ centralRegulatorId }) => this.store.select(centralRegulatorLogoById(centralRegulatorId))),
            mergeMap(([{ centralRegulatorId }, logo]) => {
                if (!logo.url) {
                    return this.loadCentralRegulatorLogo(centralRegulatorId).pipe(
                        map((result) => LoadCentralRegulatorLogoSuccess({ centralRegulatorLogoUrl: result, centralRegulatorId })),
                        catchError((error) =>
                            of({ type: DocumentActionTypes.LoadCentralRegulatorLogoFailure, error: error, centralRegulatorId })
                        )
                    );
                }
                return of({ type: DocumentActionTypes.LoadCentralRegulatorLogoSuccess, centralRegulatorLogoUrl: logo, centralRegulatorId });
            })
        )
    );

    loadcentralRegulatorLogoExtra$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCentralRegulatorLogoExtra),
            concatLatestFrom(({ centralRegulatorId }) => this.store.select(centralRegulatorLogoExtraById(centralRegulatorId))),
            mergeMap(([{ centralRegulatorId }, logo]) => {
                if (!logo.url) {
                    return this.loadCentralRegulatorLogoExtra(centralRegulatorId).pipe(
                        map((result) => LoadCentralRegulatorLogoExtraSuccess({ centralRegulatorLogoExtraUrl: result, centralRegulatorId })),
                        catchError((error) =>
                            of({
                                type: DocumentActionTypes.LoadCentralRegulatorLogoExtraFailure,
                                error: error,
                                centralRegulatorId
                            })
                        )
                    );
                }
                return of({
                    type: DocumentActionTypes.LoadCentralRegulatorLogoExtraSuccess,
                    centralRegulatorLogoExtraUrl: logo,
                    centralRegulatorId
                });
            })
        )
    );

    uploadCentralRegulatorLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadCentralRegulatorLogo),
            mergeMap(({ centralRegulatorId, file }) =>
                this.uploadCentralRegulatorLogo(centralRegulatorId, file).pipe(
                    map(() => ({ type: DocumentActionTypes.LoadCentralRegulatorLogo, centralRegulatorId })),
                    catchError((error) =>
                        of({ type: DocumentActionTypes.UploadCentralRegulatorLogoFailure, error: error, centralRegulatorId })
                    )
                )
            )
        )
    );

    uploadCentralRegulatorLogoExtra$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadCentralRegulatorLogoExtra),
            mergeMap(({ centralRegulatorId, file }) =>
                this.uploadCentralRegulatorLogoExtra(centralRegulatorId, file).pipe(
                    map(() => ({ type: DocumentActionTypes.LoadCentralRegulatorLogoExtra, centralRegulatorId })),
                    catchError((error) =>
                        of({
                            type: DocumentActionTypes.UploadCentralRegulatorLogoExtraFailure,
                            error: error,
                            centralRegulatorId
                        })
                    )
                )
            )
        )
    );

    deleteCentralRegulatorLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCentralRegulatorLogo),
            mergeMap(({ centralRegulatorId }) =>
                this.apiService.deleteCentralRegulatorLogo(centralRegulatorId).pipe(
                    map(() => ({ type: DocumentActionTypes.DeleteCentralRegulatorLogoSuccess, centralRegulatorId })),
                    catchError((error) => of({ type: DocumentActionTypes.DeleteCentralRegulatorLogoFailure, error: error }))
                )
            )
        )
    );

    deleteCentralRegulatorLogoExtra$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCentralRegulatorLogoExtra),
            mergeMap(({ centralRegulatorId }) =>
                this.apiService.deleteCentralRegulatorLogoExtra(centralRegulatorId).pipe(
                    map(() => ({ type: DocumentActionTypes.DeleteCentralRegulatorLogoExtraSuccess, centralRegulatorId })),
                    catchError((error) => of({ type: DocumentActionTypes.DeleteCentralRegulatorLogoExtraFailure, error: error }))
                )
            )
        )
    );
    constructor(
        private actions$: Actions,
        private store: Store,
        private apiService: DocumentControllerXService,
        private sanitizer: DomSanitizer
    ) {}

    private loadDocument(document: DocumentDescriptionDTO): Observable<Blob> {
        if (!document.key) return of();

        switch (document.bucket) {
            case 'employees':
                return this.apiService.downloadEmployeeDocument(document.key).pipe(take(1));
            case 'companies':
                return this.apiService.downloadCompanyDocument(document.key).pipe(take(1));
            case 'products':
                return this.apiService.downloadProductDocument(document.key).pipe(take(1));
            case 'central-regulators':
                return this.apiService.downloadCentralRegulatorDocument(document.key).pipe(take(1));
            default:
                return this.apiService.downloadEmployeeDocument(document.key).pipe(take(1));
        }
    }

    private loadDocumentAsSafeResourceUrl(document: DocumentDescriptionDTO): Observable<SafeResourceUrl> {
        return this.loadDocument(document).pipe(
            take(1),
            map((blob) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob)))
        );
    }

    private loadCompanyLogo(companyId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadCompanyLogo(companyId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private loadCompanyLogoNoCache(companyId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadCompanyLogoNoCache(companyId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private loadCompanyLogoExtra(companyId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadCompanyLogoExtra(companyId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private loadCompanyLogoExtraNoCache(companyId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadCompanyLogoExtraNoCache(companyId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private uploadCompanyLogo(companyId: string, file: File): Observable<object> {
        return this.apiService.uploadCompanyLogo(companyId, file);
    }

    private uploadCompanyLogoExtra(companyId: string, file: File): Observable<object> {
        return this.apiService.uploadCompanyLogoExtra(companyId, file);
    }

    private loadHealthInsurerLogo(healthInsurerId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadHealthInsurerLogo(healthInsurerId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private downloadHealthInsurerLogo(healthInsurerId: string): Observable<Blob> {
        return this.apiService.downloadHealthInsurerLogo(healthInsurerId).pipe(take(1));
    }

    private uploadHealthInsurerLogo(healthInsurerId: string, file: File): Observable<object> {
        return this.apiService.uploadHealthInsurerLogo(healthInsurerId, file);
    }

    private loadProfilePicture(employeeId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadProfilePicture(employeeId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private loadCentralRegulatorLogo(centralRegulatorId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadCentralRegulatorLogo(centralRegulatorId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private loadCentralRegulatorLogoExtra(centralRegulatorId: string): Observable<SafeResourceUrl> {
        return this.apiService.downloadCentralRegulatorLogoExtra(centralRegulatorId).pipe(
            take(1),
            map((res) => this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res)))
        );
    }

    private uploadCentralRegulatorLogo(centralRegulatorId: string, file: File): Observable<object> {
        return this.apiService.uploadCentralRegulatorLogo(centralRegulatorId, file);
    }

    private uploadCentralRegulatorLogoExtra(centralRegulatorId: string, file: File): Observable<object> {
        return this.apiService.uploadCentralRegulatorLogoExtra(centralRegulatorId, file);
    }
}
